import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';

import { Modal } from '../../../components/BootstrapToMUI';
import request from '../../../services/Request';

const QUICK = [50, 100, 500, 1000];

export default function BulkPointsModal(props) {
  const { members = [], id, setReflesh, reflesh, ...rest } = props;

  const [mode, setMode] = useState('add');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (props.show) {
      setMode('add');
      setAmount('');
    }
  }, [props.show]);

  const value = Number(amount);
  const valid = amount !== '' && Number.isFinite(value) && !(mode === 'set' && value < 0);

  const handleSubmit = async () => {
    if (!valid) {
      return Swal.fire({ text: 'Please enter a valid number of points.', icon: 'warning' });
    }

    const confirm = await Swal.fire({
      title: mode === 'add' ? 'Add points?' : 'Overwrite points?',
      html:
        mode === 'add'
          ? `<b>${value > 0 ? '+' : ''}${value}</b> points will be applied to <b>${
              members.length
            }</b> member(s). Balances never go below 0.`
          : `Every selected member's balance will be <b>replaced</b> with <b>${value}</b>. ` +
            `Their current points will be lost.<br/><br/><b>${members.length}</b> member(s) affected.`,
      icon: mode === 'add' ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonText: mode === 'add' ? 'Add points' : 'Overwrite',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const res = await request({
        method: 'patch',
        url: '/clientUser/bulkEditPoints',
        data: {
          ids: members.map((m) => m._id),
          referTo: id,
          mode,
          amount: value,
        },
      });

      await Swal.fire({
        title: 'Success',
        text: `${res.data.modified} of ${res.data.requested} member(s) updated.`,
        icon: 'success',
      });
      props.onHide();
      setReflesh(!reflesh);
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.error?.message || 'Something went wrong',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal className="modal-w" {...rest} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">Edit points in bulk</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {members.length} member(s) selected
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3, maxHeight: 96, overflowY: 'auto' }}>
            {members.slice(0, 30).map((m) => (
              <Chip key={m._id} size="small" label={`${m.username} (${m.points || 0})`} />
            ))}
            {members.length > 30 && (
              <Chip size="small" variant="outlined" label={`+${members.length - 30} more`} />
            )}
          </Box>

          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)} sx={{ mb: 1 }}>
            <FormControlLabel value="add" control={<Radio />} label="Add to current balance" />
            <FormControlLabel value="set" control={<Radio />} label="Set to a fixed value" />
          </RadioGroup>

          {mode === 'set' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This replaces each member's balance. Use <b>Add</b> if you just want to top accounts up.
            </Alert>
          )}

          <TextField
            fullWidth
            type="number"
            label={mode === 'add' ? 'Points to add (negative to deduct)' : 'New points balance'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={mode === 'set' ? { min: 0 } : {}}
            sx={{ mb: 2 }}
          />

          {mode === 'add' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {QUICK.map((n) => (
                <Button key={n} size="small" variant="outlined" onClick={() => setAmount(String(n))}>
                  +{n}
                </Button>
              ))}
              {QUICK.map((n) => (
                <Button
                  key={`-${n}`}
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setAmount(String(-n))}
                >
                  -{n}
                </Button>
              ))}
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={!valid || saving || members.length === 0}
            onClick={handleSubmit}
          >
            {saving ? 'Saving...' : `Apply to ${members.length} member(s)`}
          </Button>
        </Box>
      </Modal.Body>
    </Modal>
  );
}
